/** Color-space conversions used by the Tier 3 color picker.
 *
 *  Surface:
 *    - `parseHex(hex)` → RGB tuple, or null if malformed
 *    - `rgbToHex(r,g,b)` → `#rrggbb`
 *    - `rgbToHsb` / `hsbToRgb` (HSV; H 0–360, S/B 0–100)
 *    - `rgbToHsl` / `hslToRgb` (H 0–360, S/L 0–100)
 *    - `rgbToLab` / `labToRgb` (CIELAB D65; L 0–100, a/b roughly −128..127)
 *
 *  All RGB channels are integers 0..255. All conversions are lossy
 *  in the worst case (sRGB gamut clips out-of-range Lab values) so
 *  the picker must round-trip through the canonical hex
 *  representation when committing, not store HSB / HSL / Lab as the
 *  source of truth. The popover keeps a draft per mode tab to absorb
 *  rounding error during a typing session without snapping the
 *  cursor when the user pauses on a value the round-trip would
 *  reshape.
 *
 *  References:
 *    - HSB / HSL conversions: standard formulas (no external dep)
 *    - CIELAB via D65 sRGB → XYZ → Lab — same path Adobe / GIMP use
 *      so values round-trip with their pickers within rounding. */

export type Rgb = { r: number; g: number; b: number };
export type Hsb = { h: number; s: number; b: number };
export type Hsl = { h: number; s: number; l: number };
export type Lab = { l: number; a: number; b: number };

/** Parse `#rgb` / `#rrggbb` to an `Rgb` tuple, or `null` for any
 *  malformed input (including non-strings). The picker uses this to
 *  validate paste / autocomplete in the Hex tab — invalid input
 *  leaves the model unchanged. */
export function parseHex(raw: string): Rgb | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  const v = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (v.length === 3) {
    if (!/^[0-9a-fA-F]{3}$/.test(v)) return null;
    return {
      r: parseInt(v[0]! + v[0]!, 16),
      g: parseInt(v[1]! + v[1]!, 16),
      b: parseInt(v[2]! + v[2]!, 16),
    };
  }
  if (v.length === 6) {
    if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
    return {
      r: parseInt(v.slice(0, 2), 16),
      g: parseInt(v.slice(2, 4), 16),
      b: parseInt(v.slice(4, 6), 16),
    };
  }
  return null;
}

/** Serialize an `Rgb` tuple as `#rrggbb` (lowercase). Channels are
 *  clamped + rounded to 0..255 so callers can pass float-y values
 *  coming out of the HSB / Lab math without an extra clamp. */
export function rgbToHex({ r, g, b }: Rgb): string {
  const h = (n: number) => clamp255(n).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function clamp255(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, Math.round(n)));
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

// ─── HSB (HSV) ──────────────────────────────────────────────────

/** RGB (0..255) → HSB. H is 0..360 (0 for gray), S/B are 0..100. */
export function rgbToHsb({ r, g, b }: Rgb): Hsb {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rN) h = ((gN - bN) / d) % 6;
    else if (max === gN) h = (bN - rN) / d + 2;
    else h = (rN - gN) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;
  return { h, s, b: v };
}

/** HSB → RGB (0..255 integers). Accepts H beyond 0..360 (modulo)
 *  and clamps S/B into 0..100. */
export function hsbToRgb({ h, s, b }: Hsb): Rgb {
  const hMod = ((h % 360) + 360) % 360;
  const sN = clamp(s, 0, 100) / 100;
  const vN = clamp(b, 0, 100) / 100;
  const c = vN * sN;
  const hh = hMod / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = vN - c;
  return {
    r: clamp255((r1 + m) * 255),
    g: clamp255((g1 + m) * 255),
    b: clamp255((b1 + m) * 255),
  };
}

// ─── HSL ────────────────────────────────────────────────────────

/** RGB (0..255) → HSL. H 0..360, S/L 0..100. */
export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === rN) h = ((gN - bN) / d) % 6;
    else if (max === gN) h = (bN - rN) / d + 2;
    else h = (rN - gN) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}

/** HSL → RGB (0..255). Accepts H modulo 360 and clamps S/L. */
export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const hMod = ((h % 360) + 360) % 360;
  const sN = clamp(s, 0, 100) / 100;
  const lN = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const hh = hMod / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = lN - c / 2;
  return {
    r: clamp255((r1 + m) * 255),
    g: clamp255((g1 + m) * 255),
    b: clamp255((b1 + m) * 255),
  };
}

// ─── CIELAB (D65) ───────────────────────────────────────────────

/** D65 white point in XYZ (CIE 1931 2°). */
const D65 = { x: 95.047, y: 100, z: 108.883 };

function srgbToLinear(c: number): number {
  const n = c / 255;
  return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return v * 255;
}

function fLab(t: number): number {
  const delta = 6 / 29;
  return t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta ** 2) + 4 / 29;
}

function fLabInv(t: number): number {
  const delta = 6 / 29;
  return t > delta ? t ** 3 : 3 * delta ** 2 * (t - 4 / 29);
}

/** RGB (0..255) → CIELAB. L 0..100, a/b roughly −128..127. */
export function rgbToLab({ r, g, b }: Rgb): Lab {
  const rL = srgbToLinear(r);
  const gL = srgbToLinear(g);
  const bL = srgbToLinear(b);
  // sRGB → XYZ (D65). Coefficients from the standard sRGB matrix,
  // scaled to a Y=100 normalization to match the D65 reference white
  // used below.
  const x = (rL * 0.4124564 + gL * 0.3575761 + bL * 0.1804375) * 100;
  const y = (rL * 0.2126729 + gL * 0.7151522 + bL * 0.072175) * 100;
  const z = (rL * 0.0193339 + gL * 0.119192 + bL * 0.9503041) * 100;
  const fx = fLab(x / D65.x);
  const fy = fLab(y / D65.y);
  const fz = fLab(z / D65.z);
  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

/** CIELAB → RGB (0..255 integers, clamped to sRGB gamut). Out-of-
 *  gamut Lab values silently clip rather than throwing — the picker
 *  uses the clipped result as the displayed swatch. */
export function labToRgb({ l, a, b }: Lab): Rgb {
  const fy = (l + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;
  const x = D65.x * fLabInv(fx);
  const y = D65.y * fLabInv(fy);
  const z = D65.z * fLabInv(fz);
  // XYZ → linear sRGB
  const rL = (x * 3.2404542 + y * -1.5371385 + z * -0.4985314) / 100;
  const gL = (x * -0.969266 + y * 1.8760108 + z * 0.041556) / 100;
  const bL = (x * 0.0556434 + y * -0.2040259 + z * 1.0572252) / 100;
  return {
    r: clamp255(linearToSrgb(Math.max(0, Math.min(1, rL)))),
    g: clamp255(linearToSrgb(Math.max(0, Math.min(1, gL)))),
    b: clamp255(linearToSrgb(Math.max(0, Math.min(1, bL)))),
  };
}

// ─── Convenience round-trips ────────────────────────────────────

/** `hex → Hsb` shortcut. Returns null for malformed input. */
export function hexToHsb(hex: string): Hsb | null {
  const rgb = parseHex(hex);
  return rgb ? rgbToHsb(rgb) : null;
}

/** `Hsb → hex` shortcut. */
export function hsbToHex(hsb: Hsb): string {
  return rgbToHex(hsbToRgb(hsb));
}
